/**
 * Consent Service – Phase 3B.
 *
 * Encapsulates all API calls to the consent ledger endpoints at
 * ``/api/v1/consent/*``.  When the backend is unavailable the service falls
 * back to deterministic mock data so the UI remains fully functional in local
 * development.
 *
 * Camel ↔ snake conversion is handled here so types/consent-types.ts stays
 * free of backend naming conventions.
 */

import apiClient from '../api/api-client';
import { AxiosError } from 'axios';
import type {
  ConsentEvent,
  ConsentRequest,
  ConsentVerification,
  GrantConsentPayload,
  DenyConsentPayload,
  RevokeConsentPayload,
} from '../types/consent-types';

function isBackendUnavailable(err: unknown): boolean {
  if (err instanceof AxiosError) {
    return !err.response || (err.response.status >= 500 && err.response.status < 600);
  }
  return false;
}

// ---------------------------------------------------------------------------
// Base path
// ---------------------------------------------------------------------------

const BASE = '/v1/consent';

// ---------------------------------------------------------------------------
// Mapping helpers
// ---------------------------------------------------------------------------

/** Map a raw API response object to a ``ConsentEvent``. */
function mapEvent(raw: Record<string, unknown>): ConsentEvent {
  const scope = (raw.scope ?? {}) as Record<string, unknown>;
  return {
    id: String(raw.id),
    eventHash: String(raw.event_hash ?? ''),
    prevHash: raw.prev_hash != null ? String(raw.prev_hash) : null,
    action: raw.action as ConsentEvent['action'],
    providerName: String(raw.provider_name ?? ''),
    providerOrganization: String(raw.provider_organization ?? ''),
    scope: {
      recordTypes: (scope.record_types as string[]) ?? [],
      recordIds: scope.record_ids as string[] | undefined,
    },
    purpose: String(raw.purpose ?? ''),
    expiresAt: raw.expires_at != null ? String(raw.expires_at) : null,
    patientSignature:
      raw.patient_signature != null ? String(raw.patient_signature) : undefined,
    status: raw.status as ConsentEvent['status'],
    createdAt: String(raw.created_at ?? new Date().toISOString()),
  };
}

/** Map a pending request event to a ``ConsentRequest`` view-model. */
function mapRequest(event: ConsentEvent): ConsentRequest {
  return {
    id: event.id,
    providerName: event.providerName,
    providerOrganization: event.providerOrganization,
    requestedRecordTypes: event.scope.recordTypes,
    purpose: event.purpose,
    requestedDuration: 'Pending approval',
    requestedAt: event.createdAt,
    status: 'pending',
  };
}

// ---------------------------------------------------------------------------
// Mock data (used when the backend is unavailable)
// ---------------------------------------------------------------------------

const MOCK_PATIENT_ID = 'mock-patient-uuid';

const mockEvents: ConsentEvent[] = [
  {
    id: '101',
    eventHash: 'a1b2c3d4e5f6a1b2c3d4e5f6a1b2c3d4e5f6a1b2c3d4e5f6a1b2c3d4e5f6a1b2',
    prevHash: null,
    action: 'grant',
    providerName: 'Dr. Sarah Chen',
    providerOrganization: 'KaiMed Cardiology',
    scope: { recordTypes: ['lab_results', 'vitals', 'ecg_reports'] },
    purpose: 'Ongoing cardiac care',
    expiresAt: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString(),
    status: 'active',
    createdAt: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: '102',
    eventHash: 'b2c3d4e5f6a1b2c3d4e5f6a1b2c3d4e5f6a1b2c3d4e5f6a1b2c3d4e5f6a1b2c3',
    prevHash: 'a1b2c3d4e5f6a1b2c3d4e5f6a1b2c3d4e5f6a1b2c3d4e5f6a1b2c3d4e5f6a1b2',
    action: 'grant',
    providerName: 'Dr. James Okonkwo',
    providerOrganization: 'General Practice Partners',
    scope: { recordTypes: ['medical_history', 'medications', 'allergies'] },
    purpose: 'Second opinion consultation',
    expiresAt: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString(),
    status: 'active',
    createdAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: '103',
    eventHash: 'c3d4e5f6a1b2c3d4e5f6a1b2c3d4e5f6a1b2c3d4e5f6a1b2c3d4e5f6a1b2c3d4',
    prevHash: 'b2c3d4e5f6a1b2c3d4e5f6a1b2c3d4e5f6a1b2c3d4e5f6a1b2c3d4e5f6a1b2c3',
    action: 'deny',
    providerName: 'Pharma Research Inc.',
    providerOrganization: 'Pharma Research Inc.',
    scope: { recordTypes: ['genomics', 'lab_results'] },
    purpose: 'Research study participation',
    expiresAt: null,
    status: 'denied',
    createdAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
  },
];

const mockPendingRequests: ConsentRequest[] = [
  {
    id: '201',
    providerName: 'Dr. Amara Patel',
    providerOrganization: 'Metro Radiology Center',
    requestedRecordTypes: ['imaging_reports', 'radiology_notes'],
    purpose: 'Review imaging for potential surgical planning',
    requestedDuration: '60 days',
    requestedAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
    status: 'pending',
  },
  {
    id: '202',
    providerName: 'Dr. Liam Fischer',
    providerOrganization: 'Endocrinology Associates',
    requestedRecordTypes: ['lab_results', 'medications', 'vitals'],
    purpose: 'Diabetes management follow-up',
    requestedDuration: '1 year',
    requestedAt: new Date(Date.now() - 18 * 60 * 60 * 1000).toISOString(),
    status: 'pending',
  },
];

const mockVerification: ConsentVerification = {
  isValid: true,
  eventCount: mockEvents.length,
  patientId: MOCK_PATIENT_ID as unknown as number, // ConsentVerification.patientId kept as number for API compat
  verifiedAt: new Date().toISOString(),
  violations: [],
};

// ---------------------------------------------------------------------------
// Service methods
// ---------------------------------------------------------------------------

/**
 * Fetch pending consent requests for a patient.
 * Falls back to mock data if the API is unreachable.
 */
export async function getConsentRequests(
  patientId: string,
): Promise<ConsentRequest[]> {
  try {
    const { data } = await apiClient.get<Array<Record<string, unknown>>>(
      `${BASE}/history`,
      { params: { patient_id: patientId } },
    );
    const events = data.map(mapEvent);
    return events
      .filter((e) => e.action === 'request' && e.status === 'pending')
      .map(mapRequest);
  } catch (err) {
    if (isBackendUnavailable(err)) {
      if (import.meta.env.PROD) {
        return [];
      }
      return mockPendingRequests;
    }
    throw err;
  }
}

/**
 * Fetch the full consent audit trail for a patient.
 * Falls back to mock data if the API is unreachable.
 */
export async function getConsentHistory(
  patientId: string,
  skip = 0,
  limit = 100,
): Promise<ConsentEvent[]> {
  try {
    const { data } = await apiClient.get<{
      events: Array<Record<string, unknown>>;
    }>(`${BASE}/history`, { params: { patient_id: patientId, skip, limit } });
    return data.events.map(mapEvent);
  } catch (err) {
    if (isBackendUnavailable(err)) {
      if (import.meta.env.PROD) {
        return [];
      }
      return mockEvents;
    }
    throw err;
  }
}

/**
 * Fetch currently active grant events for a patient.
 * Falls back to filtered mock data if the API is unreachable.
 */
export async function getActiveConsents(
  patientId: string,
): Promise<ConsentEvent[]> {
  try {
    const { data } = await apiClient.get<Array<Record<string, unknown>>>(
      `${BASE}/active`,
      { params: { patient_id: patientId } },
    );
    return data.map(mapEvent);
  } catch (err) {
    if (isBackendUnavailable(err)) {
      if (import.meta.env.PROD) {
        return [];
      }
      return mockEvents.filter(
        (e) => e.action === 'grant' && e.status === 'active',
      );
    }
    throw err;
  }
}

/**
 * Grant a consent request.
 *
 * @param patientId - The patient granting consent.
 * @param requestId - Numeric ID of the pending request event (optional).
 * @param scope - Record types being granted.
 * @param durationDays - Number of days the grant is valid for (null = indefinite).
 */
export async function grantConsent(
  payload: GrantConsentPayload,
): Promise<ConsentEvent> {
  const body = {
    patient_id: payload.patientId,
    request_event_id: payload.requestEventId,
    provider_id: payload.providerId,
    provider_name: payload.providerName,
    provider_organization: payload.providerOrganization,
    scope: {
      record_types: payload.scope.recordTypes,
      record_ids: payload.scope.recordIds,
    },
    purpose: payload.purpose,
    expires_in_days: payload.expiresInDays,
  };
  const { data } = await apiClient.post<Record<string, unknown>>(
    `${BASE}/grant`,
    body,
  );
  return mapEvent(data);
}

/**
 * Deny a consent request.
 */
export async function denyConsent(
  payload: DenyConsentPayload,
): Promise<ConsentEvent> {
  const body = {
    patient_id: payload.patientId,
    request_event_id: payload.requestEventId,
    provider_id: payload.providerId,
    provider_name: payload.providerName,
    provider_organization: payload.providerOrganization,
    scope: {
      record_types: payload.scope.recordTypes,
      record_ids: payload.scope.recordIds,
    },
    purpose: payload.purpose,
  };
  const { data } = await apiClient.post<Record<string, unknown>>(
    `${BASE}/deny`,
    body,
  );
  return mapEvent(data);
}

/**
 * Revoke a previously granted consent.
 *
 * @param eventId - Numeric ID of the grant event to revoke.
 * @param patientId - Must match the patient on the event.
 */
export async function revokeConsent(
  eventId: string,
  payload: RevokeConsentPayload,
): Promise<ConsentEvent> {
  const body = { patient_id: payload.patientId };
  const { data } = await apiClient.post<Record<string, unknown>>(
    `${BASE}/revoke/${eventId}`,
    body,
  );
  return mapEvent(data);
}

/**
 * Verify the integrity of the patient's consent chain.
 * Falls back to a mock "valid" result if the API is unreachable.
 */
export async function verifyChain(
  patientId: string,
): Promise<ConsentVerification> {
  try {
    const { data } = await apiClient.get<Record<string, unknown>>(
      `${BASE}/verify`,
      { params: { patient_id: patientId } },
    );
    return {
      isValid: Boolean(data.is_valid),
      eventCount: Number(data.event_count),
      patientId: Number(data.patient_id),
      verifiedAt: String(data.verified_at),
      violations: (data.violations as ConsentVerification['violations']) ?? [],
    };
  } catch (err) {
    if (isBackendUnavailable(err)) {
      if (import.meta.env.PROD) {
        return {
          isValid: false,
          eventCount: 0,
          patientId: patientId as unknown as number,
          verifiedAt: new Date().toISOString(),
          violations: [],
        };
      }
      return mockVerification;
    }
    throw err;
  }
}
